import { sendGAEvent } from './gaConsent';

export const trackButtonClick = (
  category: string,
  action = 'click',
  label: string,
  additionalParams = {},
) => {
  try {
    sendGAEvent({
      category,
      action,
      label,
      ...additionalParams,
    });
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

export const trackHomeButtonClick = (buttonName: string, buttonId: string) => {
  trackEvent({
    name: 'home_cta_click',
    buttonId,
    label: buttonName,
    page: 'home',
    section: 'hero',
    feature: 'home',
    eventCategory: 'home',
  });
};

export const trackFeedbackAction = (action: string) => {
  trackEvent({
    name: action,
    buttonId: `feedback_${action}`,
    label: 'Feedback button',
    page: 'global',
    section: 'feedback',
    feature: 'feedback',
    eventCategory: 'feedback',
  });
};

export const trackBrowseTabClick = (tabName: string) => {
  trackEvent({
    name: 'tab_click',
    buttonId: `browse_tab_${tabName}`,
    label: tabName,
    page: 'browse',
    section: 'tabs',
    feature: 'browse',
    eventCategory: 'browse',
  });
};

export const trackRowDetailTabClick = (tabName: string) => {
  trackEvent({
    name: 'tab_click',
    buttonId: `row_detail_tab_${tabName}`,
    label: tabName,
    page: 'search_results',
    section: 'bottom_detail',
    feature: 'row_detail',
    eventCategory: 'search',
  });
};

export const trackSearchBrowserTabAction = (action: string, label?: string) => {
  trackEvent({
    name: `search_tab_${action}`,
    buttonId: `search_browser_tab_${action}`,
    label: label ?? action,
    page: 'search_results',
    section: 'search_browser_tabs',
    feature: 'search_tabs',
    eventCategory: 'search',
  });
};

export const trackFilterAction = (action: string) => {
  trackEvent({
    name: action,
    buttonId: `filters_${action}`,
    label: 'Manage filters',
    page: 'search_results',
    section: 'filters',
    feature: 'filters',
    eventGroup: 'search',
    eventCategory: 'search',
  });
};

export const trackSaveFilterAction = (action: string) => {
  trackEvent({
    name: action,
    buttonId: `save_filters_${action}`,
    label: 'Save filters',
    page: 'search_results',
    section: 'filters',
    feature: 'filters',
    eventGroup: 'search',
    eventCategory: 'search',
  });
};

export const trackEnrichmentAction = (action: string, source = '') => {
  trackEvent({
    name: action,
    buttonId: source ? `enrichment_${action}_${source}` : `enrichment_${action}`,
    label: source ? `${action}_${source}` : action,
    page: 'enrichment',
    section: 'workflow',
    feature: 'enrichment',
    eventCategory: 'enrichment',
  });
};

export const slugifyTrackingValue = (value = ''): string =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const trackEvent = ({
  name,
  buttonId,
  label,
  page = '',
  section = '',
  feature = '',
  eventGroup = 'engagement',
  eventCategory = '',
  additionalParams = {},
}: {
  name: string;
  buttonId: string;
  label?: string;
  page?: string;
  section?: string;
  feature?: string;
  eventGroup?: string;
  eventCategory?: string;
  additionalParams?: Record<string, any>;
}) => {
  const normalizedId = slugifyTrackingValue(buttonId) || 'unknown_click';
  const normalizedName = slugifyTrackingValue(name) || 'interaction';
  const normalizedFeature = slugifyTrackingValue(feature) || 'general';
  const normalizedPage = slugifyTrackingValue(page) || 'unknown';
  const normalizedSection = slugifyTrackingValue(section) || 'general';
  const normalizedCategory = slugifyTrackingValue(eventCategory) || normalizedFeature;

  sendGAEvent({
    category: normalizedCategory,
    action: normalizedName,
    label: label ?? normalizedId,

    event_category: normalizedCategory,
    event_group: slugifyTrackingValue(eventGroup) || 'engagement',
    page: normalizedPage,
    section: normalizedSection,
    feature: normalizedFeature,
    button_id: normalizedId,
    custom_parameter_1: normalizedId,
    ...additionalParams,
  });
};

export const trackClickWithId = (
  category: string,
  buttonId: string,
  label: string,
  additionalParams = {},
) => {
  trackEvent({
    name: `${slugifyTrackingValue(category)}_click`,
    buttonId,
    label,
    feature: category,
    eventCategory: category,
    additionalParams,
  });
};
